import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Stack, Typography } from '@mui/material';
import { Send as SendIcon, PictureAsPdf as PictureAsPdfIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { STATUS_NEW, STATUS_PROCESS } from '../../config/constants';
import SignatureModal from './SignatureModal';
import { submitInitialReport } from '../../services/jhaInitialReportService';
import buildJhaPayload from './utils/jhaPayloadAdapter';
import notificationService from '../../services/notificationService';
import fallArrestRescuePlanService from '../../services/fallArrestRescuePlanService';
import { findMissingDocumentsLocal, evaluateFarpRequirement, isFarpAttachedOrLinked, injectMissingDocRows } from './utils/documentTriggerEvaluator';
import { showMissingDocsDialog } from './utils/missingDocsDialog';
import axiosInstance from '../../axiosInstance';
import { localDateString } from '../../utils/localDate';

const TODAY_STRING = localDateString();

// Swal по умолчанию ставит .swal2-container с z-index 1060, а MUI header — 1100,
// поэтому toast перекрывается шапкой. Поднимаем контейнер выше всего.
const TOAST_DEFAULTS = {
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    didOpen: () => {
        document.querySelectorAll('.swal2-container').forEach((c) => {
            c.style.zIndex = '13000';
        });
    },
};


/**
 * Pre-shift signature + "Send Initial JHA" panel.
 *
 * Раньше этот блок жил внутри Work Activities (step3). После перестановки шагов
 * (2 = Activities, 3 = Documents) подпись и отправка переехали на шаг Documents,
 * поэтому логика вынесена в переиспользуемый компонент. Он работает с полным
 * jhaData (signature и initialSubmission хранятся в stepThree, документы — в
 * stepTwo) и сохраняется через переданные scheduleSave/flushSave хоста.
 *
 * Props:
 *   jhaData        — полная запись JHA
 *   scheduleSave   — (record, { immediate, minStep }) => void (host persistence)
 *   flushSave      — () => Promise<void>
 *   viewMode       — read-only режим просмотра
 *   isReadOnly     — read-only (submitted/locked/view)
 *   isSubmitted    — финальный submit уже сделан
 *   onSent         — optional callback после успешной отправки/постановки в очередь
 */
export default function JhaInitialSubmission({
    jhaData,
    scheduleSave,
    flushSave,
    viewMode = false,
    isReadOnly = false,
    isSubmitted = false,
    onSent,
}) {
    const navigate = useNavigate();

    const [signatureModalOpen, setSignatureModalOpen] = useState(false);
    const [sendingInitial, setSendingInitial] = useState(false);
    const [initialSendError, setInitialSendError] = useState('');

    const [farpTriggerValues, setFarpTriggerValues] = useState([]);
    const [farpExistingPlan, setFarpExistingPlan] = useState(null);
    // eslint-disable-next-line no-unused-vars
    const [farpCheckLoading, setFarpCheckLoading] = useState(false);
    // FARP required по ПОЛНОМУ оценщику (work-activity / energy / hazard).
    const [farpRequired, setFarpRequired] = useState(false);
    const [farpReason, setFarpReason] = useState('');

    const initialSent = Boolean(jhaData?.stepThree?.initialSubmission?.status === 'sent');
    const initialPending = Boolean(jhaData?.stepThree?.initialSubmission?.status === 'pending');
    const signature = jhaData?.stepThree?.preShiftSignature || '';

    // ─── canSendInitial — те же проверки что и в Work Activities ──────────
    const canSendInitial = useMemo(() => {
        const rows = jhaData?.stepThree?.energyExposures || [];
        const hasSelection = rows.some(row =>
            Array.isArray(row.energySelections) && row.energySelections.some(sel => sel.energyId)
        );
        const allSelectionsHaveHazards = rows.every(row =>
            (row.energySelections || []).every(sel => !sel.energyId || (sel.hazards && sel.hazards.length))
        );
        const allActiveRowsHaveActivity = rows.every(row => {
            const hasEnergy = Array.isArray(row.energySelections) && row.energySelections.some(sel => sel.energyId);
            if (!hasEnergy) return true;
            return Boolean(row.workActivity && row.workActivity.trim());
        });
        return Boolean(signature && hasSelection && allSelectionsHaveHazards && allActiveRowsHaveActivity);
    }, [jhaData?.stepThree?.energyExposures, signature]);

    // ─── FARP derived state ──────────────────────────────────────────────
    const triggeredActivities = useMemo(() => {
        if (!jhaData?.stepThree?.energyExposures || farpTriggerValues.length === 0) return [];
        const triggerSet = new Set(farpTriggerValues);
        const seen = new Set();
        const result = [];
        for (const row of jhaData.stepThree.energyExposures) {
            const wa = (row.workActivity || '').trim();
            if (wa && triggerSet.has(wa) && !seen.has(wa)) {
                seen.add(wa);
                result.push(wa);
            }
        }
        return result;
    }, [jhaData?.stepThree?.energyExposures, farpTriggerValues]);

    // FARP считается выполненным, если: есть поданная цифровая форма
    // (farpExistingPlan), ИЛИ к документу приложено фото/файл, ИЛИ привязан
    // linkedFarpUuid. Это гасит баннер «required» и soft-warning «not found»,
    // когда юзер просто приложил фото готового бланка (раньше смотрели ТОЛЬКО на
    // поданную цифровую форму, поэтому фото игнорировалось).
    const farpSatisfied = useMemo(
        () => Boolean(farpExistingPlan) || isFarpAttachedOrLinked(jhaData),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [farpExistingPlan, jhaData?.stepTwo]
    );

    const farpJobCode = jhaData?.stepOne?.jobNumber || '';
    const farpDate = jhaData?.stepOne?.date || TODAY_STRING;
    const farpJhaLocalId = jhaData?.localId || '';

    useEffect(() => {
        (async () => {
            const values = await fallArrestRescuePlanService.getTriggerValues();
            setFarpTriggerValues(values);
        })();
    }, []);

    // FARP required — по ПОЛНОМУ оценщику (work-activity / energy / hazard),
    // чтобы баннер и soft-warning показывались и для energy/hazard-триггеров.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const { required, reason } = await evaluateFarpRequirement(jhaData);
            if (!cancelled) { setFarpRequired(required); setFarpReason(reason); }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jhaData?.stepThree, jhaData?.stepOne]);

    useEffect(() => {
        if (!farpRequired) { setFarpExistingPlan(null); return; }
        if (!farpJhaLocalId && (!farpJobCode || !farpDate)) { setFarpExistingPlan(null); return; }
        let cancelled = false;
        (async () => {
            setFarpCheckLoading(true);
            const criteria = farpJhaLocalId
                ? { jhaLocalId: farpJhaLocalId }
                : { jobCode: farpJobCode, date: farpDate };
            const { exists, plan } = await fallArrestRescuePlanService.checkExisting(criteria);
            if (cancelled) return;
            setFarpExistingPlan(exists ? plan : null);
            setFarpCheckLoading(false);
        })();
        return () => { cancelled = true; };
    }, [farpRequired, farpJhaLocalId, farpJobCode, farpDate]);

    const openFarpForm = () => {
        const params = new URLSearchParams();
        if (farpJobCode) params.set('jobCode', farpJobCode);
        if (farpDate) params.set('planDate', farpDate);
        if (farpJhaLocalId) params.set('jhaLocalId', farpJhaLocalId);
        // returnTo — обратно на шаг Documents (новый шаг 3)
        const returnTo = jhaData?.id
            ? `/jha/step-3?id=${encodeURIComponent(jhaData.id)}`
            : '/jha';
        params.set('returnTo', returnTo);
        navigate(`/safety/forms/fall-arrest-rescue-plan/form?${params.toString()}`);
    };

    const viewExistingFarpPdf = async () => {
        if (!farpExistingPlan?.id) return;
        try {
            const resp = await axiosInstance.get(`/fall-arrest-rescue-plan/${farpExistingPlan.id}/pdf`, {
                responseType: 'blob',
            });
            const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
            window.open(url, '_blank');
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (e) {
            Swal.fire('Error', 'Failed to load PDF: ' + (e.message || 'unknown'), 'error');
        }
    };

    const handleSignatureSave = signatureData => {
        if (!jhaData || isSubmitted) return;
        const updated = { ...jhaData, stepThree: { ...jhaData.stepThree, preShiftSignature: signatureData } };
        if (updated.status === STATUS_NEW) updated.status = STATUS_PROCESS;
        scheduleSave(updated, { immediate: true, minStep: 3 });
        setSignatureModalOpen(false);
    };

    const handleSignatureClear = () => {
        if (!jhaData || isSubmitted) return;
        const updated = { ...jhaData, stepThree: { ...jhaData.stepThree, preShiftSignature: '' } };
        scheduleSave(updated, { immediate: true, minStep: 3 });
    };

    const handleInitialSend = async () => {
        if (!jhaData || isSubmitted || sendingInitial) return;

        if (initialSent) {
            const result = await Swal.fire({
                title: 'Resend Initial JHA?',
                text: 'This will overwrite the previously submitted initial report. All data will be replaced with the current form values.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#ff9800',
                cancelButtonColor: '#6c757d',
                confirmButtonText: 'Yes, resend it',
                cancelButtonText: 'Cancel'
            });
            if (!result.isConfirmed) return;
        }

        // ─── Soft warning: FARP требуется, но НЕ выполнен ничем (ни фото, ни
        // привязкой, ни поданной формой). Если приложено фото бланка — farpSatisfied
        // true и эта модалка НЕ показывается. ──────────
        if (farpRequired && !farpSatisfied) {
            const triggers = triggeredActivities.length ? triggeredActivities.join(', ') : farpReason;
            const result = await Swal.fire({
                title: 'Fall Arrest Rescue Plan not found',
                html: `Selected work activities require a rescue plan:<br><br>` +
                      `<em>${triggers}</em><br><br>` +
                      `No submitted Fall Arrest Rescue Plan exists for this job (` +
                      `<strong>${farpJobCode || 'no job'}</strong>) on <strong>${farpDate}</strong>.<br><br>` +
                      `You can submit the JHA now and file the rescue plan later, or open the rescue plan form first.`,
                icon: 'warning',
                showDenyButton: true,
                showCancelButton: true,
                confirmButtonText: 'Open Rescue Plan',
                confirmButtonColor: '#d32f2f',
                denyButtonText: 'Submit anyway',
                denyButtonColor: '#ff9800',
                cancelButtonText: 'Cancel',
                didOpen: () => {
                    document.querySelectorAll('.swal2-container').forEach((c) => { c.style.zIndex = '13000'; });
                },
            });
            if (result.isConfirmed) { openFarpForm(); return; }
            if (!result.isDenied) return; // Cancel
            Swal.fire({ ...TOAST_DEFAULTS, icon: 'warning', title: 'Reminder: file the Fall Arrest Rescue Plan ASAP', timer: 4500, timerProgressBar: true });
        }

        setInitialSendError('');

        // Pre-flight: trigger-required docs without attachment/link block the send.
        // Мы уже на шаге Documents, поэтому просто показываем что добавить (без навигации).
        try {
            const missing = await findMissingDocumentsLocal(jhaData);
            if (missing.length > 0) {
                await showMissingDocsDialog(missing);
                return; // блокируем отправку
            }
        } catch (e) {
            console.warn('[JhaInitialSubmission] pre-flight trigger check failed (continuing):', e);
        }

        setSendingInitial(true);

        try {
            await flushSave();
            const requestSubmittedAt = new Date().toISOString();

            // Offline → save as pending
            if (!navigator.onLine) {
                const updated = {
                    ...jhaData,
                    status: jhaData.status === STATUS_NEW ? STATUS_PROCESS : jhaData.status,
                    stepThree: {
                        ...jhaData.stepThree,
                        initialSubmission: { status: 'pending', pendingSince: requestSubmittedAt }
                    },
                    meta: { ...(jhaData?.meta || {}), lastStep: Math.max(jhaData?.meta?.lastStep || 1, 4) }
                };
                scheduleSave(updated, { immediate: true, minStep: 4 });
                await notificationService.requestPermission();
                await Swal.fire({
                    icon: 'info',
                    title: 'No Internet Connection',
                    html: `<div style="text-align: left;"><p><strong>Your initial report has been saved locally.</strong></p>` +
                        `<p>It will be automatically sent when your internet connection is restored.</p>` +
                        `<ul style="margin-top: 12px;"><li>You can continue filling out the form</li>` +
                        `<li>Your data is safely stored on this device</li><li>No action is required from you</li>` +
                        `<li>You will receive a notification when it's sent</li></ul></div>`,
                    confirmButtonText: 'Continue',
                    confirmButtonColor: '#2196f3',
                    allowOutsideClick: false
                });
                setSendingInitial(false);
                if (onSent) onSent(updated);
                return;
            }

            // Online
            const initialReportData = { ...jhaData, stepFour: undefined, stepFive: undefined, submittedAt: requestSubmittedAt };
            const payload = buildJhaPayload(initialReportData) || {};
            payload.submittedAt = requestSubmittedAt;

            const response = await submitInitialReport(payload);
            const submittedAt = response?.submittedAt || requestSubmittedAt;
            const reportId = response?.id ?? null;

            const updated = {
                ...jhaData,
                status: jhaData.status === STATUS_NEW ? STATUS_PROCESS : jhaData.status,
                stepThree: {
                    ...jhaData.stepThree,
                    initialSubmission: { status: 'sent', submittedAt, reportId }
                },
                meta: { ...(jhaData?.meta || {}), lastStep: Math.max(jhaData?.meta?.lastStep || 1, 4) }
            };
            scheduleSave(updated, { immediate: true, minStep: 4 });

            // Auto-submit + auto-link связанного FARP.
            if (jhaData?.localId) {
                try {
                    const linked = await fallArrestRescuePlanService.findDraftByJhaLocalId(jhaData.localId);
                    if (linked && linked.submitterSignature) {
                        const farpResp = await fallArrestRescuePlanService.submit({ ...linked, status: 'submitted' });
                        const note = farpResp?.success
                            ? '✓ Linked Fall Arrest Rescue Plan submitted'
                            : (farpResp?.queued ? '⌛ Linked Fall Arrest Rescue Plan queued (offline)' : null);
                        if (note) Swal.fire({ ...TOAST_DEFAULTS, icon: 'success', title: note, timer: 3000 });
                    } else if (linked && !linked.submitterSignature) {
                        Swal.fire({ ...TOAST_DEFAULTS, icon: 'warning', title: 'Linked Fall Arrest Rescue Plan needs signature — not auto-submitted', timer: 4500, timerProgressBar: true });
                    }
                } catch (e) {
                    console.warn('Auto-submit linked FARP failed:', e);
                }
            }
            if (onSent) onSent(updated);
        } catch (error) {
            console.error('Failed to send initial JHA report', error);
            const status = error?.response?.status;
            const data = error?.response?.data || {};
            const missing = Array.isArray(data.missing) ? data.missing : [];
            if (status === 422 && missing.length > 0) {
                // Бэкенд требует документ(ы), которых может НЕ быть в локальном списке
                // (локальный оценщик не сматчил триггер — energy/hazard на бэке
                // резолвятся через связи БД). Добавляем недостающие строки, чтобы
                // юзеру было КУДА приложить фото / открыть форму. Иначе документ
                // "required", но строки нет → тупик (кейс FARP в поле).
                if (!isReadOnly) {
                    const { stepTwo, added } = injectMissingDocRows(jhaData?.stepTwo, missing);
                    if (added.length) {
                        const updated = { ...jhaData, stepTwo };
                        if (updated.status === STATUS_NEW) updated.status = STATUS_PROCESS;
                        scheduleSave(updated, { immediate: true, minStep: 3 });
                    }
                }
                await showMissingDocsDialog(missing);
                setInitialSendError('Missing required documents — see dialog for details.');
            } else {
                const message = data.message || data.error || error?.message || 'Failed to send initial report';
                setInitialSendError(message);
            }
        } finally {
            setSendingInitial(false);
        }
    };

    return (
        <Stack spacing={1} sx={{ mt: 1 }}>
            {/* FARP status banner — clear UX for the rescue plan requirement */}
            {farpRequired && (
                <Alert
                    severity={farpSatisfied ? 'success' : 'warning'}
                    variant="outlined"
                    sx={{ py: 0.5 }}
                    action={
                        farpExistingPlan ? (
                            <Button color="inherit" size="small" startIcon={<PictureAsPdfIcon />} onClick={viewExistingFarpPdf}>
                                View FARP
                            </Button>
                        ) : (!farpSatisfied && !isReadOnly && (
                            <Button color="inherit" size="small" onClick={openFarpForm}>
                                Open Rescue Plan
                            </Button>
                        ))
                    }
                >
                    {farpExistingPlan
                        ? `Fall Arrest Rescue Plan on file (${farpExistingPlan.planDate || farpDate}${farpExistingPlan.submitterName ? ' · ' + farpExistingPlan.submitterName : ''}).`
                        : farpSatisfied
                            ? `Fall Arrest Rescue Plan attached — requirement met.`
                            : `Fall Arrest Rescue Plan required — triggered by ${triggeredActivities.length ? triggeredActivities.join(', ') : farpReason}.`}
                </Alert>
            )}

            <Card elevation={1}>
                <CardContent sx={{ py: 1.5 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Stack spacing={0.5} flexGrow={1}>
                            <Typography variant="subtitle2">Pre-Shift Signature</Typography>
                            {signature ? (
                                <Box sx={{ maxWidth: 200 }}>
                                    <img src={signature} alt="Pre shift signature" style={{ width: '100%', border: '1px solid #ccc', borderRadius: 4, maxHeight: 60 }} />
                                </Box>
                            ) : (
                                <Typography variant="caption" color="text.secondary">No signature captured</Typography>
                            )}
                        </Stack>
                        {!initialSent && !isReadOnly && (
                            <Stack direction="row" spacing={1}>
                                <Button variant="outlined" size="small" disabled={isSubmitted} onClick={() => setSignatureModalOpen(true)}>
                                    {signature ? 'Update' : 'Capture'}
                                </Button>
                                {signature && (
                                    <Button variant="text" color="error" onClick={handleSignatureClear}>Clear</Button>
                                )}
                            </Stack>
                        )}
                    </Stack>
                </CardContent>
            </Card>

            <Card elevation={1}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                        <Typography variant="h6" sx={{ fontSize: '1rem', flexShrink: 0 }}>Initial JHA Submission</Typography>
                        {!isReadOnly && (
                            <Button
                                variant="contained"
                                endIcon={<SendIcon />}
                                disabled={!canSendInitial || sendingInitial}
                                onClick={handleInitialSend}
                                size="small"
                                sx={{ flexShrink: 0, ml: 'auto', ...(initialSent ? { backgroundColor: '#ff9800', '&:hover': { backgroundColor: '#f57c00' } } : {}) }}
                            >
                                {sendingInitial ? 'Sending…' : (initialSent ? 'Resend' : 'Send Initial JHA')}
                            </Button>
                        )}
                    </Box>
                    {initialSent ? (
                        <Alert severity="success" variant="outlined" sx={{ py: 0.5 }}>
                            Initial JHA sent {new Date(jhaData.stepThree.initialSubmission.submittedAt).toLocaleString()}.
                        </Alert>
                    ) : initialPending ? (
                        <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
                            Initial JHA saved offline — will send automatically when back online.
                        </Alert>
                    ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                            Capture the pre-shift signature and complete task details to send the initial JHA.
                        </Typography>
                    )}
                    {initialSendError && (
                        <Alert severity="error" sx={{ mt: 1 }}>{initialSendError}</Alert>
                    )}
                </CardContent>
            </Card>

            <SignatureModal
                open={signatureModalOpen}
                onClose={() => setSignatureModalOpen(false)}
                onSave={handleSignatureSave}
            />
        </Stack>
    );
}
